/**
 * Reklam hesabı bağlama sözleşmesi (v3.1 CA-4 — gerçek SALT-OKUNUR reklam hesabı bağlama; önce sosyal medya reklamları).
 * Belge: docs/spec/castinflow/ad-account-connect.md. Karar: ADR-0016 § 1, § 3, § 8 ("Castinflow reklam hesabına yazmaz").
 *
 * Akış (yetki kodu, sunucu tarafı değişim):
 *   1. `authorize` — tek kullanımlık `state` üretilir ve sağlayıcının onay adresi döner (sağlayıcıya çağrı YOK).
 *   2. Tarayıcı onay ekranından uygulama içi dönüş sayfasına gelir (`AD_CONNECT_CALLBACK_PATH`; GET yan etkisiz).
 *   3. `exchange` — `state` doğrulanır, yetki kodu belirteçle değiştirilir, erişilebilen reklam hesapları listelenir. Belirteç
 *      yanıta GİRMEZ: zarf şifreli, kısa ömürlü, tek kullanımlık bir "seçim bileti"nde tutulur.
 *   4. `connect` — seçilen hesaplar için bağlantı oluşturulur (hesap başına bir bağlantı) ya da bu çalışma alanında zaten bağlı
 *      hesabın yetkisi yenilenir (yeniden bağlanma: varlık ve geçmiş korunur). YENİ hesaplar reklam hesabı alt kotasına
 *      (`ad_accounts`; K2) sayılır: seçilen yeni hesap sayısı kalan yeri aşıyorsa istek BÜTÜNÜYLE reddedilir (`PLAN_LIMIT`,
 *      ayrıntıda `remaining`); yenileme sayılmaz, kesilmiş bağlantı yer tutmaz.
 *
 * İstenen kapsam YALNIZ okuma kapsamıdır (`AD_CONNECT_SCOPE`). Hiçbir yanıt yetki kodu, belirteç ya da tam dış hesap kimliği
 * taşımaz: hesap seçimi bilet kapsamında anlamlı OPAK kimlikle yapılır, dış kimlik maskeli gösterilir. Arayüz metinlerinde
 * üçüncü taraf marka adı kullanılmaz (genel kategori adları); sözleşme anahtarları kod tanımlayıcısıdır.
 */
import { z } from 'zod';
import { planQuotaStatusSchema } from './plans.js';
import { PROVIDER_CONNECTION_STATUSES, SIMULATOR_SCENARIOS, providerConnectionViewSchema, } from './provider-connections.js';
/** Onay ekranından dönüş sayfası (uygulama içi rota; sağlayıcıya verilen yönlendirme adresi `<web kökü>` + bu yol — TAM eşleşme) */
export const AD_CONNECT_CALLBACK_PATH = '/settings/connections/ads/callback';
/** İstenen tek kapsam: reklam raporlarını OKUMA */
export const AD_CONNECT_SCOPE = 'ads_read';
export const AD_CONNECT_LIMITS = {
    /** `state` rastgele bayt sayısı (≥ 32) */
    stateBytes: 32,
    /** `state` ömrü (saniye; süre esaslı) */
    stateTtlSeconds: 600,
    /** Seçim bileti ömrü (saniye; süre esaslı) */
    ticketTtlSeconds: 600,
    /** Tek istekte bağlanabilecek / yenilenebilecek hesap */
    maxSelect: 20,
    /** Hesap listesinde okunan en çok sayfa ve sayfa boyu (aşan kısım `truncated`) */
    accountPages: 5,
    accountPageSize: 100,
    /** Kullanıcı başına pencere içinde en çok `authorize` ve `exchange` */
    authorizePerWindow: 20,
    exchangePerWindow: 10,
    rateWindowSeconds: 600,
};
/** `state` ve bilet: URL-güvenli base64 (32 bayt → 43 karakter) */
export const AD_CONNECT_OPAQUE_RE = /^[A-Za-z0-9_-]{43,128}$/;
/** Hesap seçim kimliği (bilet kapsamında opak) */
export const AD_CONNECT_ACCOUNT_KEY_RE = /^[A-Za-z0-9_-]{12,32}$/;
/** Reklam hesabının dış kimliği (yalnız rakam; `act_` öneksiz) */
export const AD_ACCOUNT_EXTERNAL_ID_RE = /^\d{3,20}$/;
/** Yetki kodu: boşluksuz, yazdırılabilir ASCII (sağlayıcı biçimi opaktır) */
const AUTH_CODE_RE = /^[\x21-\x7E]{8,4096}$/;
export const AD_CONNECT_MODES = ['live', 'simulator'];
/**
 * Hesap durumu (sağlayıcının sayısal `account_status` değerinin kapalı küme karşılığı): 1 etkin · 2 devre dışı · 3 ödeme
 * bekliyor · 7 risk incelemesinde · 8 mutabakat bekliyor · 9 ek süre · 100 kapanış bekliyor · 101 kapalı.
 */
export const AD_ACCOUNT_STATUSES = [
    'active',
    'disabled',
    'unsettled',
    'pending_review',
    'pending_settlement',
    'grace_period',
    'pending_closure',
    'closed',
    'unknown',
];
const STATUS_BY_CODE = {
    1: 'active',
    2: 'disabled',
    3: 'unsettled',
    7: 'pending_review',
    8: 'pending_settlement',
    9: 'grace_period',
    100: 'pending_closure',
    101: 'closed',
};
export function adAccountStatusOf(code) {
    return code === null || code === undefined ? 'unknown' : (STATUS_BY_CODE[code] ?? 'unknown');
}
/**
 * Bağlama akışına özgü ret nedenleri (`error.details.reason`). Sağlayıcının ham yanıtı taşınmaz.
 *   provider_not_available  gerçek kip bu süreçte kapalı ("onay bekliyor")
 *   simulator_disabled      simülatör bu ortamda kapalı
 *   state_invalid           `state` yok, kullanılmış, süresi dolmuş ya da başka kullanıcıya/oturuma ait
 *   code_rejected           sağlayıcı yetki kodunu kabul etmedi (kullanılmış / süresi dolmuş / yönlendirme adresi uyuşmuyor)
 *   provider_rejected       sağlayıcı sonraki bir okumayı reddetti (belirteç kabul edilmedi / istek reddedildi)
 *   scope_missing           okuma kapsamı verilmedi (kullanıcı onay ekranında izni kaldırdı)
 *   ticket_invalid          seçim bileti yok, kullanılmış, süresi dolmuş ya da başka kullanıcıya/oturuma ait
 *   account_not_in_ticket   seçilen hesap bu biletin listesinde yok
 *   asset_already_bound     hesap başka bir çalışma alanına bağlı
 *   authorization_superseded  yetkilendirme başladıktan sonra seçilen hesabın bağlantısı değişti (kesildi / kesiliyor, başka
 *                             bir oturum yetkisini yeniledi ya da hesap bu arada bağlandı): oturum o hesap için geçersizdir
 *                             (v3.2 B; ortak anlam `connect-session.ts`)
 */
export const AD_CONNECT_REASONS = [
    'provider_not_available',
    'simulator_disabled',
    'state_invalid',
    'code_rejected',
    'provider_rejected',
    'scope_missing',
    'ticket_invalid',
    'account_not_in_ticket',
    'asset_already_bound',
    'authorization_superseded',
];
// ---------------------------------------------------------------------------------------------------------------------
// API sözleşmesi
// ---------------------------------------------------------------------------------------------------------------------
/** `POST /settings/provider-connections/ads/meta/authorize` */
export const adConnectAuthorizeInputSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('live') }).strict(),
    z
        .object({
        mode: z.literal('simulator'),
        /** Simülatörün sağlayıcı tarafı senaryosu (`expiring` = belirteç 3 gün sonra doluyor) */
        scenario: z.enum(SIMULATOR_SCENARIOS).default('valid'),
    })
        .strict(),
]);
export const adConnectAuthorizeResultSchema = z.object({
    /** Gidilecek adres: gerçek kipte sağlayıcının onay ekranı (tam adres), simülatörde uygulama içi dönüş yolu */
    url: z.string().min(1).max(4000),
    /** `true` = sağlayıcı adresi (tam sayfa yönlendirme); `false` = uygulama içi yol (simülatör) */
    external: z.boolean(),
    /** `state` geçerlilik bitişi (UTC, ISO 8601) */
    expiresAt: z.string(),
});
/** `POST /settings/provider-connections/ads/meta/exchange` */
export const adConnectExchangeInputSchema = z
    .object({
    code: z.string().regex(AUTH_CODE_RE, 'geçersiz yetki kodu'),
    state: z.string().regex(AD_CONNECT_OPAQUE_RE, 'geçersiz durum değeri'),
})
    .strict();
export const adConnectAccountSchema = z.object({
    /** Seçim kimliği (bilet kapsamında opak; dış hesap kimliği DEĞİLDİR) */
    id: z.string().regex(AD_CONNECT_ACCOUNT_KEY_RE),
    /** Maskeli dış hesap kimliği (son 4 karakter) */
    externalIdMasked: z.string(),
    name: z.string().nullable(),
    /** ISO 4217 */
    currency: z.string().nullable(),
    /** IANA saat dilimi (hesabın rapor günü bu dilimdedir) */
    timeZone: z.string().nullable(),
    status: z.enum(AD_ACCOUNT_STATUSES),
    /** Başka bir çalışma alanına bağlı: seçilemez (hangi çalışma alanı olduğu söylenmez) */
    alreadyBound: z.boolean(),
    /** Bu çalışma alanında zaten bağlıysa bağlantısı: seçilirse YETKİSİ YENİLENİR (varlık ve geçmiş korunur) */
    connection: z
        .object({
        id: z.string(),
        displayName: z.string(),
        status: z.enum(PROVIDER_CONNECTION_STATUSES),
    })
        .nullable(),
});
export const adConnectExchangeResultSchema = z.object({
    /** Seçim bileti (tek kullanımlık; belirteç bu değerde YOKTUR — sunucuda zarf şifreli tutulur) */
    ticket: z.string().regex(AD_CONNECT_OPAQUE_RE),
    /** Biletin geçerlilik bitişi (UTC, ISO 8601) */
    expiresAt: z.string(),
    simulated: z.boolean(),
    accounts: z.array(adConnectAccountSchema),
    /** Hesap listesi okuma sınırını aştı: yalnız ilk hesaplar gösteriliyor */
    truncated: z.boolean(),
    /**
     * Reklam hesabı alt kotasının anlık durumu (K2): kaç YENİ hesap bağlanabileceği (`remaining`; `null` = sınırsız). Bu çalışma
     * alanında zaten bağlı hesabın seçilmesi (yetki yenileme) yer harcamaz. Bilgilendirir; karar `connect` anında sunucudadır.
     * Eski sunucu yanıtında alan yoktur.
     */
    quota: planQuotaStatusSchema.optional(),
});
/** `POST /settings/provider-connections/ads/meta/connect` */
export const adConnectConnectInputSchema = z
    .object({
    ticket: z.string().regex(AD_CONNECT_OPAQUE_RE, 'geçersiz seçim bileti'),
    /** `exchange` yanıtındaki hesap seçim kimlikleri */
    accountIds: z
        .array(z.string().regex(AD_CONNECT_ACCOUNT_KEY_RE, 'geçersiz hesap seçimi'))
        .min(1, 'en az bir hesap seçin')
        .max(AD_CONNECT_LIMITS.maxSelect),
    /** Bağlantı adı: yalnız TEK yeni hesap bağlanırken verilebilir (verilmezse hesabın adı) */
    displayName: z.string().trim().min(1).max(120).optional(),
})
    .strict()
    .refine((v) => new Set(v.accountIds).size === v.accountIds.length, {
    message: 'aynı hesap iki kez seçilemez',
    path: ['accountIds'],
})
    .refine((v) => v.displayName === undefined || v.accountIds.length === 1, {
    message: 'bağlantı adı yalnız tek hesap seçildiğinde verilebilir',
    path: ['displayName'],
});
export const adConnectConnectResultSchema = z.object({
    /** Oluşturulan ve yetkisi yenilenen bağlantılar (seçim sırasıyla) */
    connections: z.array(providerConnectionViewSchema),
    created: z.number().int(),
    renewed: z.number().int(),
});
/**
 * Dönüş adresindeki sorgu dizgisinin sınıflandırılması (yan etkisiz; istemci kullanır). Kullanıcı onayı reddettiyse
 * (`error=access_denied`) `denied`; başka sağlayıcı hatası `error`; `code`/`state` yoksa ya da biçimsizse `invalid`.
 */
export function parseAdConnectCallback(search) {
    const q = new URLSearchParams(search);
    const error = q.get('error');
    if (error !== null && error !== '')
        return error === 'access_denied' ? { kind: 'denied' } : { kind: 'error' };
    const parsed = adConnectExchangeInputSchema.safeParse({
        code: q.get('code'),
        state: q.get('state'),
    });
    return parsed.success ? { kind: 'code', ...parsed.data } : { kind: 'invalid' };
}
//# sourceMappingURL=ad-account-connect.js.map